import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { CheckboxInput, Typography } from "@/components/atoms";
import { useGetVehicleFormOptionsQuery } from "@/redux/apis/vehicle/vehicleApi";
import { FilterDrawer } from "../common/FilterDrawer";
import { FilterPopover } from "../common/FilterPopover";
import { useVehicleFilters } from "../useVehicleFilters";

const schema = z.object({
  vehicleType: z.array(z.string()),
});

type FormData = z.infer<typeof schema>;

const useVehicleTypeDisplayValue = (selected: (string | null)[] | null | undefined) => {
  const { data: options } = useGetVehicleFormOptionsQuery();

  return useMemo(() => {
    if (!selected) return undefined;
    const names = selected
      .filter(Boolean)
      .map((uuid) => options?.vehicleTypes.find((t) => t.uuid === uuid)?.name)
      .filter(Boolean) as string[];
    return names.length > 0 ? names.join(", ") : undefined;
  }, [selected, options]);
};

export const VehicleTypeFilter = () => {
  const {
    filters: { vehicleType },
    setFilter,
  } = useVehicleFilters();

  const value = useVehicleTypeDisplayValue(vehicleType);

  const onClose = () => setFilter("vehicleType", undefined);

  return (
    <FilterPopover name="vehicleType" value={value} label="Vehicle Type" onClose={onClose}>
      <VehicleTypeFilterForm />
    </FilterPopover>
  );
};

interface VehicleTypeFilterDrawerProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const VehicleTypeFilterDrawer = ({ open, onOpenChange }: VehicleTypeFilterDrawerProps) => {
  const {
    filters: { vehicleType },
    setFilter,
  } = useVehicleFilters();

  const value = useVehicleTypeDisplayValue(vehicleType);

  const onClose = () => setFilter("vehicleType", undefined);

  return (
    <FilterDrawer open={open} onOpenChange={onOpenChange} value={value} label="Vehicle Type" onClose={onClose}>
      <VehicleTypeFilterForm />
    </FilterDrawer>
  );
};

const VehicleTypeFilterForm = () => {
  const { setFilter, filters } = useVehicleFilters();
  const { isFetching, data: options } = useGetVehicleFormOptionsQuery();

  const types = useMemo(() => {
    if (isFetching || !options) return [];
    return options.vehicleTypes.map((type) => ({ name: type.name, value: type.uuid }));
  }, [isFetching, options]);

  const { control, setValue } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { vehicleType: (filters.vehicleType as string[]) ?? [] },
  });

  const handleCheckAll = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = e.target;

      if (checked) {
        setFilter(
          "vehicleType",
          types.map((type) => type.value)
        );
        setValue(
          "vehicleType",
          types.map((type) => type.value)
        );
      } else {
        setFilter("vehicleType", null);
        setValue("vehicleType", []);
      }
    },
    [types, setFilter, setValue]
  );

  const isAllSelected = useMemo(() => {
    if (!filters.vehicleType) return false;
    return filters.vehicleType.length === types.length;
  }, [types.length, filters.vehicleType]);

  return (
    <div className="contents">
      <Typography className="mb-4">Vehicle Type</Typography>
      <CheckboxInput label="All Vehicle Types" onChange={handleCheckAll} variant="check" checked={isAllSelected} />
      <div className="mt-4 grid gap-4 ">
        <Controller
          control={control}
          name="vehicleType"
          render={({ field }) => (
            <>
              {types.map((type) => (
                <CheckboxInput
                  key={type.value}
                  label={type.name}
                  value={type.value}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const next = [...field.value, e.target.value];
                      field.onChange(next);
                      setFilter("vehicleType", next.length > 0 ? next : null);
                    } else {
                      const next = field.value.filter((i) => i !== e.target.value);
                      field.onChange(next);
                      setFilter("vehicleType", next.length > 0 ? next : null);
                    }
                  }}
                  variant="check"
                  checked={field.value.includes(type.value)}
                />
              ))}
            </>
          )}
        />
      </div>
    </div>
  );
};
